import { yupResolver } from '@hookform/resolvers/yup';
import { useUserEventLogger } from '@repo/tracking-service';
import { Button, Fieldset, TextField, Title } from '@zenterprise-inc/ui';
import { ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';

import { useBothApplyRefunds } from '../common/hooks/use-both-apply-refund';

import { getRefundDataForWebStorage } from '@/components/tax-refund/common/utils/refund-data';
import { applyRefundPhoneNumberFormSchema } from '@/lib/regex/schema/apply-phone-number';

export const PhoneNumberContent = ({ businessType }: { businessType: 'indis' | 'corps' }) => {
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(applyRefundPhoneNumberFormSchema)
  });
  const { bothApplyRefunds, isInFlight, isInFlightTritx } = useBothApplyRefunds();
  const { sendClickEvent } = useUserEventLogger();

  const handleChangePhone = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value }
    } = e;
    const newValue = value
      .trim()
      .replaceAll('-', '')
      .replace(/^(\d{3})(\d{3,4})(\d{4})$/, `$1-$2-$3`);

    setValue('phone', newValue);
  };

  const onSubmit = () => {
    sendClickEvent(businessType === 'corps' ? 'corps-request_phonenumber_next' : 'indis-request_phonenumber_next');

    const { phone } = getValues();

    const sessionStorageRefundData = getRefundDataForWebStorage(businessType);

    const variables = {
      refundId:
        businessType === 'indis'
          ? sessionStorageRefundData.indis?.refundId || sessionStorageRefundData.indis?.tritxRefundInfo?.refundId || ''
          : sessionStorageRefundData.corps?.refundId || '',
      phoneNumber: phone?.replaceAll('-', ''),
      bank: sessionStorageRefundData[businessType]?.bank || '',
      accountOwner: sessionStorageRefundData[businessType]?.accountOwner || '',
      accountNo: sessionStorageRefundData[businessType]?.accountNo || ''
    };

    const tritxRefundAmount = businessType === 'indis' ? (sessionStorageRefundData.indis?.tritxRefundInfo?.refundAmount ?? '0') : '0';

    bothApplyRefunds({
      variables,
      refundAmountTotal: Number(sessionStorageRefundData[businessType]?.refundAmount ?? 0) + Number(tritxRefundAmount),
      refundAmount: Number(sessionStorageRefundData[businessType]?.refundAmount ?? 0),
      businessType
    });
  };

  return (
    <div className="page-padding flex w-full flex-col">
      <Title.Section title={'마지막으로 환급 안내를 위해\n휴대폰번호를 확인해주세요'} />
      <Fieldset.Wrapper isInvalid={!!errors.phone} className="w-full">
        <Fieldset.Label>휴대폰 번호</Fieldset.Label>
        <TextField.Basic
          maxLength={20}
          pattern="\d*"
          inputMode="numeric"
          type="text"
          className="w-full"
          placeholder="000-0000-0000"
          {...register('phone', {
            required: true,
            onChange: handleChangePhone,
            onBlur: () => trigger('phone')
          })}
          error={!!errors.phone}
          onClick={() => sendClickEvent(businessType === 'corps' ? 'corps-request_phonenumber_input-number' : 'indis-request_phonenumber_input-number')}
        />
        <Fieldset.Error>올바른 휴대폰 번호를 입력해주세요.</Fieldset.Error>
      </Fieldset.Wrapper>

      <Button.StickyBottom.Wrapper className="page-padding">
        <Button.StickyBottom.Button disabled={!isValid} state="primary" onClick={handleSubmit(onSubmit)} loading={isInFlight || isInFlightTritx}>
          확인
        </Button.StickyBottom.Button>
      </Button.StickyBottom.Wrapper>
    </div>
  );
};
